/**
 * Simplifies a reservation to its pricing-relevant parts
 * This extracts the minimal data needed for price calculations
 */
import helpers from '@/services/helpers';

interface SimplifiedHall {
  pax: number;
  startTime: string;
  endTime: string;
  hall: string | number;
}

interface SimplifiedItem {
  amount: number;
  item: string;
}

interface SimplifiedPackageDeal {
  amount: number;
  packageDeal: string;
}

interface SimplifiedDay {
  date: string;
  startTime: string;
  endTime: string;
  pax: number;
  halls?: SimplifiedHall[];
  items?: SimplifiedItem[];
  packageDeals?: SimplifiedPackageDeal[];
}

interface SimplifiedRoom {
  startDate: string;
  endDate: string;
  amount: number;
  paxRoom: number;
  roomType: string | number;
}

interface SimplifiedReservation {
  days?: SimplifiedDay[];
  rooms?: SimplifiedRoom[];
}

export function simplifyReservation(
  res: Record<string, any>,
): SimplifiedReservation {
  if (!res) {
    return {};
  }

  const simplified: SimplifiedReservation = {};

  // Simplify days
  if (res.days && Array.isArray(res.days)) {
    simplified.days = res.days.map((day: any) => ({
      date: day.date,
      startTime: day.startTime,
      endTime: day.endTime,
      pax: day.pax,
      halls:
        day.halls && Array.isArray(day.halls)
          ? day.halls.map((h: any) => ({
              pax: h.pax,
              startTime: h.startTime,
              endTime: h.endTime,
              hall: helpers.getIRI(h.hall, '/api/halls/'),
            }))
          : [],
      items:
        day.items && Array.isArray(day.items)
          ? day.items.map((i: any) => ({
              amount: i.amount,
              item: helpers.getIRI(i.item, '/api/items/'),
            }))
          : [],
      packageDeals:
        day.packageDeals && Array.isArray(day.packageDeals)
          ? day.packageDeals.map((p: any) => ({
              amount: p.amount,
              packageDeal: helpers.getIRI(p.packageDeal, '/api/package_deals/'),
            }))
          : [],
    }));
  }

  // Simplify rooms
  if (res.rooms && Array.isArray(res.rooms)) {
    simplified.rooms = res.rooms.map((room: any) => ({
      startDate: room.startDate,
      endDate: room.endDate,
      amount: room.amount,
      paxRoom: room.paxRoom,
      roomType:
        room.roomType ??
        helpers.getIRI(room.locationRoomType, '/api/location_room_types/'),
    }));
  }

  return simplified;
}

/**
 * Generate a string representation of a reservation for comparison
 */
export function getReservationSignature(
  res: Record<string, any> | null | undefined,
): string {
  if (!res) {
    return '';
  }
  return JSON.stringify(simplifyReservation(res));
}

/**
 * Check if a reservation has changed in a way that affects pricing
 */
export function hasReservationPricingChanged(
  newReservation: Record<string, any> | null | undefined,
  oldReservation: Record<string, any> | null | undefined,
): boolean {
  // Handle the case where one or both reservations are null/undefined
  if (!newReservation && !oldReservation) {
    return false; // No change if both are empty
  }

  if (!newReservation || !oldReservation) {
    return true; // Change detected if only one is empty
  }

  // Compare the string signatures of both reservations
  const newSignature = getReservationSignature(newReservation);
  const oldSignature = getReservationSignature(oldReservation);
  return newSignature !== oldSignature;
}

export default function useReservationPricing() {
  return {
    simplifyReservation,
    getReservationSignature,
    hasReservationPricingChanged,
  };
}
